import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Card, Spinner, useToast } from './ui';
import type { Language } from '../types';

const SHOP_TYPES = ['supermarket', 'boulangerie', 'butcher', 'fishmonger', 'market'] as const;
type ShopType = (typeof SHOP_TYPES)[number];

export interface ShoppingTemplate {
  weekday: number;
  shop_type: ShopType;
  required: boolean;
  enabled: boolean;
}

const T = {
  en: {
    title: 'Shopping days',
    intro:
      'Which days you normally shop. This is what the planner works around: it will not put a fresh fish dinner three days after the last chance to buy fish.',
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    shops: {
      supermarket: 'Supermarket',
      boulangerie: 'Boulangerie',
      butcher: 'Butcher',
      fishmonger: 'Fishmonger',
      market: 'Market',
    } as Record<ShopType, string>,
    hint: {
      supermarket: 'The main shop — everything the week needs',
      boulangerie: 'Bread and pastries, eaten the same morning — this is what makes a bakery breakfast possible',
      butcher: 'Better meat, and cuts the supermarket does not have',
      fishmonger: 'Fresh fish; without this the fish meals come from the supermarket',
      market: 'Seasonal fruit and vegetables',
    } as Record<ShopType, string>,
    required: 'Always happens',
    requiredHelp:
      'A shop marked "always happens" is one the plan can rely on. An optional one is only used if the week needs it.',
    save: 'Save shopping days',
    saved: 'Saved. The next plan will work around these.',
    none: 'No shopping days set — the planner has nothing to work with, so add at least a main shop.',
    tapHelp: 'Tap a day to turn that shop on or off.',
  },
  fr: {
    title: 'Jours de courses',
    intro:
      'Les jours où vous faites normalement les courses. Le planificateur s’organise autour : il ne mettra pas un poisson frais trois jours après la dernière occasion d’en acheter.',
    days: ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'],
    shops: {
      supermarket: 'Supermarché',
      boulangerie: 'Boulangerie',
      butcher: 'Boucherie',
      fishmonger: 'Poissonnerie',
      market: 'Marché',
    } as Record<ShopType, string>,
    hint: {
      supermarket: 'Les courses principales — tout ce dont la semaine a besoin',
      boulangerie: 'Pain et viennoiseries, pour le matin même — c’est ce qui rend possible le petit-déjeuner de la boulangerie',
      butcher: 'De la meilleure viande, et des morceaux introuvables en supermarché',
      fishmonger: 'Poisson frais ; sans cela il vient du supermarché',
      market: 'Fruits et légumes de saison',
    } as Record<ShopType, string>,
    required: 'Toujours',
    requiredHelp:
      'Une course « toujours » est une course sur laquelle le plan peut compter. Une course facultative n’est utilisée que si la semaine en a besoin.',
    save: 'Enregistrer les jours de courses',
    saved: 'Enregistré. Le prochain plan en tiendra compte.',
    none: 'Aucun jour de courses — ajoutez au moins des courses principales.',
    tapHelp: 'Touchez un jour pour activer ou désactiver cette course.',
  },
} as const;

const key = (weekday: number, shop: ShopType) => `${weekday}:${shop}`;

/**
 * Shopping days (§24, §27).
 *
 * Set once and left alone, like the school run. It matters more than it looks:
 * the whole plan is built around when food can actually be bought, so a week
 * with no fishmonger quietly becomes a week where fresh fish only works on the
 * days near the supermarket run.
 */
export function ShoppingDaysCard({ language, readOnly }: { language: Language; readOnly: boolean }) {
  const t = T[language];
  const toast = useToast();
  const [rows, setRows] = useState<Map<string, ShoppingTemplate> | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const result = await api.household();
    const templates = (result.shoppingTemplates ?? []) as ShoppingTemplate[];
    setRows(
      new Map(
        templates
          .filter((row) => SHOP_TYPES.includes(row.shop_type))
          .map((row) => [key(row.weekday, row.shop_type), row]),
      ),
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!rows) {
    return (
      <Card title={t.title}>
        <Spinner />
      </Card>
    );
  }

  const isOn = (weekday: number, shop: ShopType) => rows.get(key(weekday, shop))?.enabled ?? false;
  const isRequired = (shop: ShopType) =>
    [...rows.values()].some((row) => row.shop_type === shop && row.enabled && row.required);

  const toggleDay = (weekday: number, shop: ShopType) => {
    setRows((current) => {
      const next = new Map(current);
      const existing = next.get(key(weekday, shop));
      if (existing?.enabled) next.delete(key(weekday, shop));
      else {
        next.set(key(weekday, shop), {
          weekday,
          shop_type: shop,
          enabled: true,
          // A main shop is something the plan can count on; the specialists
          // are opportunities, not obligations.
          required: existing?.required ?? shop === 'supermarket',
        });
      }
      return next;
    });
  };

  const toggleRequired = (shop: ShopType, required: boolean) => {
    setRows((current) => {
      const next = new Map(current);
      for (const [k, row] of next) {
        if (row.shop_type === shop) next.set(k, { ...row, required });
      }
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.saveShoppingTemplates([...rows.values()].filter((row) => row.enabled));
      toast(t.saved);
      void load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const anyEnabled = [...rows.values()].some((row) => row.enabled);

  return (
    <Card title={t.title}>
      <p className="dim" style={{ marginBottom: 16 }}>
        {t.intro}
      </p>

      {!anyEnabled && <p className="banner warn">{t.none}</p>}

      <div className="stack" style={{ gap: 18 }}>
        {SHOP_TYPES.map((shop) => (
          <div key={shop} className="shop-block">
            <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <h3 className="shop-name">{t.shops[shop]}</h3>
              <span className="dim" style={{ fontSize: 13, flex: 1, minWidth: 160 }}>
                {t.hint[shop]}
              </span>
              <label className="row" style={{ gap: 8 }}>
                <input
                  type="checkbox"
                  style={{ width: 20, height: 20, minHeight: 0 }}
                  checked={isRequired(shop)}
                  disabled={readOnly || busy}
                  onChange={(event) => toggleRequired(shop, event.target.checked)}
                />
                <span className="dim">{t.required}</span>
              </label>
            </div>

            <div className="day-toggles">
              {t.days.map((dayName, weekday) => (
                <button
                  key={dayName}
                  type="button"
                  className={`day-toggle${isOn(weekday, shop) ? ' on' : ''}`}
                  aria-pressed={isOn(weekday, shop)}
                  disabled={readOnly || busy}
                  onClick={() => toggleDay(weekday, shop)}
                >
                  {dayName.slice(0, language === 'fr' ? 3 : 3)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="stack" style={{ gap: 10, marginTop: 18 }}>
          <p className="dim" style={{ fontSize: 13, margin: 0 }}>
            {t.tapHelp} {t.requiredHelp}
          </p>
          <div className="row">
            <button className="btn primary" onClick={save} disabled={busy}>
              {t.save}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
